import React, { useEffect, useRef, useState } from "react";
import { API_URL } from "../../environment";

interface Midia {
  id: number;
  id_cliente: number;
  tipo: string;
  url: string;
  nome: string | null;
  mimetype: string | null;
  id_pasta: number | null;
  created_at: string;
}
interface Pasta { id: number; tipo: string; nome: string; }

const TIPOS = [
  { key: "image", label: "Imagens", icon: "ti-photo", accept: "image/*" },
  { key: "video", label: "Vídeos", icon: "ti-video", accept: "video/*" },
  { key: "audio", label: "Áudios", icon: "ti-music", accept: "audio/*" },
  { key: "document", label: "Documentos", icon: "ti-file-text", accept: "*/*" },
];

const BibliotecaMidias: React.FC = () => {
  const [aba, setAba] = useState<string>("image");
  const [pastaAtual, setPastaAtual] = useState<Pasta | null>(null);
  const [pastas, setPastas] = useState<Pasta[]>([]);
  const [midias, setMidias] = useState<Midia[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [movendo, setMovendo] = useState<Midia | null>(null);   // mídia escolhida no modal "Mover"
  const [movendoAgora, setMovendoAgora] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const id_cliente = localStorage.getItem("id_grupo") || ""; // por GRUPO
  const id_user = localStorage.getItem("id_user") || "";

  const fetchPastas = async (tipo: string) => {
    if (!id_cliente) return;
    try {
      const res = await fetch(`${API_URL}/biblioteca/pastas?id_cliente=${id_cliente}&tipo=${tipo}`);
      setPastas(res.ok ? await res.json() : []);
    } catch (e) { console.error(e); setPastas([]); }
  };

  const fetchMidias = async (tipo: string, pasta: Pasta | null) => {
    if (!id_cliente) return;
    setLoading(true);
    try {
      const ip = pasta ? String(pasta.id) : "null"; // raiz = só sem pasta
      const res = await fetch(`${API_URL}/biblioteca?id_cliente=${id_cliente}&tipo=${tipo}&id_pasta=${ip}`);
      setMidias(res.ok ? await res.json() : []);
    } catch (e) { console.error(e); setMidias([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPastas(aba); /* eslint-disable-next-line */ }, [aba]);
  useEffect(() => { fetchMidias(aba, pastaAtual); /* eslint-disable-next-line */ }, [aba, pastaAtual]);

  const trocarAba = (tipo: string) => { setPastaAtual(null); setAba(tipo); };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!id_cliente) { alert("Grupo não identificado. Refaça o login."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo", aba);
      fd.append("id_cliente", id_cliente);
      fd.append("id_user", id_user);
      if (pastaAtual) fd.append("id_pasta", String(pastaAtual.id));
      const res = await fetch(`${API_URL}/biblioteca/upload`, { method: "POST", body: fd });
      if (res.ok) await fetchMidias(aba, pastaAtual);
      else alert("Falha no upload da mídia.");
    } catch (err) { console.error(err); alert("Erro ao enviar a mídia."); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Remover esta mídia da biblioteca?")) return;
    try {
      // id_user/id_cliente na URL: é o que faz a auditoria saber QUEM excluiu (DELETE não tem corpo)
      const res = await fetch(
        `${API_URL}/biblioteca/${id}?id_user=${id_user}&id_cliente=${id_cliente}`,
        { method: "DELETE" }
      );
      if (res.ok) setMidias((prev) => prev.filter((m) => m.id !== id));
    } catch (err) { console.error(err); }
  };

  const renomear = async (m: Midia) => {
    const atual = m.nome || "";
    const novo = window.prompt("Novo nome para esta mídia:", atual);
    if (novo === null) return;                       // cancelou
    const limpo = novo.trim();
    if (!limpo || limpo === atual) return;           // vazio ou sem mudança
    try {
      const res = await fetch(`${API_URL}/biblioteca/${m.id}/renomear`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome: limpo, id_user, id_cliente }),
      });
      if (res.ok) setMidias((prev) => prev.map((x) => (x.id === m.id ? { ...x, nome: limpo } : x)));
      else alert("Não foi possível renomear.");
    } catch (err) { console.error(err); alert("Erro ao renomear."); }
  };

  const confirmarMover = async (destino: number | null) => {
    if (!movendo) return;
    const m = movendo;
    if ((m.id_pasta ?? null) === destino) { setMovendo(null); return; }
    setMovendoAgora(true);
    try {
      const res = await fetch(`${API_URL}/biblioteca/${m.id}/mover`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_pasta: destino, id_user, id_cliente }),
      });
      if (res.ok) { setMovendo(null); await fetchMidias(aba, pastaAtual); }
      else alert("Não foi possível mover.");
    } catch (err) { console.error(err); alert("Erro ao mover."); }
    finally { setMovendoAgora(false); }
  };

  const criarPasta = async () => {
    const nome = window.prompt("Nome da nova pasta:");
    if (!nome || !nome.trim()) return;
    try {
      const res = await fetch(`${API_URL}/biblioteca/pastas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_cliente, tipo: aba, nome: nome.trim(), id_user }),
      });
      if (res.ok) await fetchPastas(aba);
      else alert("Falha ao criar a pasta.");
    } catch (err) { console.error(err); }
  };

  const excluirPasta = async (p: Pasta) => {
    if (!window.confirm(`Remover a pasta "${p.nome}"? As mídias dentro voltam para a raiz.`)) return;
    try {
      const res = await fetch(
        `${API_URL}/biblioteca/pastas/${p.id}?id_user=${id_user}&id_cliente=${id_cliente}`,
        { method: "DELETE" }
      );
      if (res.ok) { await fetchPastas(aba); if (pastaAtual?.id === p.id) setPastaAtual(null); else await fetchMidias(aba, pastaAtual); }
    } catch (err) { console.error(err); }
  };

  const tipoAtual = TIPOS.find((t) => t.key === aba) || TIPOS[0];

  const ignorarAbortVideo = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const v = e.currentTarget;
    if (v.error && v.error.code === 1) return; // MEDIA_ERR_ABORTED (seek) — benigno
  };

  const renderPreview = (m: Midia) => {
    if (m.tipo === "image") return <img src={m.url} alt={m.nome || ""} loading="lazy" decoding="async" style={{ width: "100%", height: 140, objectFit: "cover" }} />;
    if (m.tipo === "video") return <video src={m.url} controls preload="metadata" onError={ignorarAbortVideo} style={{ width: "100%", height: 140, objectFit: "cover", background: "#000" }} />;
    if (m.tipo === "audio") return <div className="d-flex align-items-center justify-content-center" style={{ height: 140, background: "#f1f5f9", padding: 12 }}><audio src={m.url} controls style={{ width: "100%" }} /></div>;
    return (
      <a href={m.url} target="_blank" rel="noreferrer" className="d-flex flex-column align-items-center justify-content-center text-decoration-none" style={{ height: 140, background: "#f1f5f9", color: "#475569" }}>
        <i className="ti ti-file-text" style={{ fontSize: 44 }} />
        <span className="small mt-2 text-truncate px-2" style={{ maxWidth: "100%" }}>{m.nome || "documento"}</span>
      </a>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content p-4">
        {/* Cabeçalho */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
          <div>
            <h4 className="mb-1 fw-bold d-flex align-items-center gap-2"><i className="ti ti-photo-video text-primary" /> Biblioteca de Mídias</h4>
            <p className="text-muted mb-0 fs-13">Organize as mídias do grupo em pastas. Disponíveis no chat pelo botão "Biblioteca".</p>
          </div>
          <div className="d-flex gap-2">
            {!pastaAtual && (
              <button className="btn btn-outline-secondary rounded-pill px-3 fw-medium" onClick={criarPasta}>
                <i className="ti ti-folder-plus me-1" /> Nova pasta
              </button>
            )}
            <input ref={fileRef} type="file" accept={tipoAtual.accept} style={{ display: "none" }} onChange={handleUpload} />
            <button className="btn btn-primary rounded-pill px-4 fw-medium" disabled={uploading} onClick={() => fileRef.current?.click()}>
              {uploading ? (<><span className="spinner-border spinner-border-sm me-2" /> Enviando...</>) : (<><i className="ti ti-upload me-1" /> Adicionar {tipoAtual.label.toLowerCase()}</>)}
            </button>
          </div>
        </div>

        {/* Abas por tipo */}
        <ul className="nav nav-pills gap-2 mb-3">
          {TIPOS.map((t) => (
            <li className="nav-item" key={t.key}>
              <button className={`nav-link d-flex align-items-center gap-2 ${aba === t.key ? "active" : ""}`} onClick={() => trocarAba(t.key)}>
                <i className={`ti ${t.icon}`} /> {t.label}
              </button>
            </li>
          ))}
        </ul>

        {/* Breadcrumb */}
        <div className="d-flex align-items-center gap-1 mb-3 fs-14">
          <button className="btn btn-link p-0 text-decoration-none fw-medium" onClick={() => setPastaAtual(null)}>{tipoAtual.label}</button>
          {pastaAtual && (<><i className="ti ti-chevron-right text-muted fs-12" /> <span className="fw-bold"><i className="ti ti-folder me-1 text-warning" />{pastaAtual.nome}</span></>)}
        </div>

        {/* Pastas (só na raiz) */}
        {!pastaAtual && pastas.length > 0 && (
          <div className="row g-3 mb-4">
            {pastas.map((p) => (
              <div className="col-6 col-md-4 col-lg-3" key={p.id}>
                <div className="card border shadow-sm h-100" style={{ cursor: "pointer" }} onClick={() => setPastaAtual(p)}>
                  <div className="card-body p-3 d-flex align-items-center justify-content-between gap-2">
                    <div className="d-flex align-items-center gap-2 overflow-hidden">
                      <i className="ti ti-folder text-warning" style={{ fontSize: 26 }} />
                      <span className="fw-medium text-truncate">{p.nome}</span>
                    </div>
                    <button className="btn btn-sm btn-outline-danger border-0 p-1" title="Remover pasta" onClick={(e) => { e.stopPropagation(); excluirPasta(p); }}>
                      <i className="ti ti-trash" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Grade de mídias */}
        {loading ? (
          <div className="text-center p-5"><div className="spinner-border text-primary" role="status" /></div>
        ) : midias.length === 0 ? (
          <div className="text-center p-5 border-dashed rounded-4 bg-light">
            <i className={`ti ${tipoAtual.icon} text-muted`} style={{ fontSize: 44 }} />
            <h6 className="fw-bold mt-3">Nenhum item {pastaAtual ? `na pasta "${pastaAtual.nome}"` : `em ${tipoAtual.label}`}</h6>
            <p className="text-muted small mb-0">Use "Adicionar" para enviar mídias{!pastaAtual ? " ou crie uma pasta." : "."}</p>
          </div>
        ) : (
          <div className="row g-3">
            {midias.map((m) => (
              <div className="col-6 col-md-4 col-lg-3" key={m.id}>
                <div className="card border shadow-sm h-100 overflow-hidden">
                  {renderPreview(m)}
                  <div className="card-body p-2 d-flex align-items-center justify-content-between gap-1">
                    <span className="small text-truncate" title={m.nome || ""}>{m.nome || "—"}</span>
                    <div className="d-flex align-items-center flex-shrink-0">
                      <button className="btn btn-sm btn-outline-secondary border-0 p-1" title="Renomear" onClick={() => renomear(m)}>
                        <i className="ti ti-pencil" />
                      </button>
                      <button className="btn btn-sm btn-outline-secondary border-0 p-1" title="Mover para pasta" onClick={() => setMovendo(m)}>
                        <i className="ti ti-folder-symlink" />
                      </button>
                      <button className="btn btn-sm btn-outline-danger border-0 p-1" title="Remover" onClick={() => handleDelete(m.id)}>
                        <i className="ti ti-trash" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal "Mover para pasta" — React puro, sem depender do JS do bootstrap */}
        {movendo && (
          <>
            <div className="modal-backdrop fade show" onClick={() => !movendoAgora && setMovendo(null)} />
            <div className="modal fade show d-block" tabIndex={-1} role="dialog">
              <div className="modal-dialog modal-dialog-centered modal-sm" role="document">
                <div className="modal-content">
                  <div className="modal-header py-2">
                    <h6 className="modal-title fw-bold"><i className="ti ti-folder-symlink me-1" /> Mover mídia</h6>
                    <button type="button" className="btn-close" disabled={movendoAgora} onClick={() => setMovendo(null)} />
                  </div>
                  <div className="modal-body">
                    <p className="small text-muted text-truncate mb-2" title={movendo.nome || ""}>{movendo.nome || "—"}</p>
                    <div className="list-group">
                      <button
                        className={`list-group-item list-group-item-action d-flex align-items-center gap-2 ${movendo.id_pasta === null ? "active" : ""}`}
                        disabled={movendoAgora}
                        onClick={() => confirmarMover(null)}
                      >
                        <i className="ti ti-home" /> {tipoAtual.label} (raiz)
                      </button>
                      {pastas.map((p) => (
                        <button
                          key={p.id}
                          className={`list-group-item list-group-item-action d-flex align-items-center gap-2 ${movendo.id_pasta === p.id ? "active" : ""}`}
                          disabled={movendoAgora}
                          onClick={() => confirmarMover(p.id)}
                        >
                          <i className="ti ti-folder text-warning" /> <span className="text-truncate">{p.nome}</span>
                        </button>
                      ))}
                    </div>
                    {pastas.length === 0 && (
                      <p className="small text-muted mt-2 mb-0">
                        Não há pastas em {tipoAtual.label.toLowerCase()}. Crie uma com "Nova pasta".
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BibliotecaMidias;
